/* eslint-disable react-hooks/exhaustive-deps */
// src/pages/buyerNet/useBuyerNetEngine.js
import { useCallback, useEffect, useRef } from "react";
import {
  getTitleEscrowCosts,
  getStateSettings,
  getCountySettings,
  getNationalSettings,
} from "../services/titleEscrowService";
import {
  getPrepaidPaymentData,
  getAmountConventional,
  getBuyerEstimatedTax,
  resultOfTitleEscrow,
  getTwoMonthPMI,
  getPSecondTd,
  getAmountFHA,
  getAmountVA,
  getAmountUSDA,
  getAmountOwnerCarry,
  computeTotals,
  setPrepaidData,
  getDaysInterestAmount,
  computeTitleFeePayorAmount,
} from "../utils/calculations";
import {
  parseCurrency,
  defaultEst,
  computeCommission,
} from "../utils/buyerNet.shared";
import { applyClosingCostOverrides } from "../utils/applyClosingCostOverrides";
import { applyWaSalesTaxClosingCosts } from "../utils/waSalesTaxClosingCosts";

export const useBuyerNetEngine = (
  formData,
  setFormData,
  templateformData,
  setTemplateformData,
  isEditLoading,
  main_company_name,
  showToaster,
  mainId,
  ttbData,
  useAnnualPropertyTax,
  getValues,
  setValue,
) => {
  // ✅ REF GUARD to prevent re-entrant calculation loops
  const isCalculatingRef = useRef(false);
  const lastCalcKeyRef = useRef("");
  const hadNationalDataRef = useRef(false);

  /**
   * ✅ Handle Address Enrichment from AddressSearchPreview
   */
  const handleAddressEnriched = useCallback(
    (data, setters) => {
      const {
        setTtbData,
        setCompanyId,
        setShowPreviewFromChild,
        setIsInitialLoad,
        setIsCorrectProperty,
      } = setters;
      const { ttb, payload, showPreview, fromParcelSelection } = data || {};
      const enrichment = payload?.enrichment;

      const propertyUnavailable = !ttb || Object.keys(ttb || {}).length === 0;

      if (propertyUnavailable) {
        console.warn(
          "AddressSearchPreview: Property not available — resetting default values",
        );

        setFormData((prev) => ({
          ...prev,
          salesPrice: "0.00",
          phase: "resetSellerDefaults",
          addressVerified: false,
          loanInfoMeta: {
            ...(prev.loanInfoMeta || {}),
            annualPropertyTax: "0.00",
            mortgageAmount: "0.00",
            homeWarrantyAmount: "0.00",
          },
          compensationMeta: {
            ...(prev.compensationMeta || {}),
            sellerBrokerCompensation: "0.00",
            buyerBrokerCompensation: "0.00",
          },
        }));

        setTtbData({});
        setIsCorrectProperty(false);

        showToaster({
          message:
            "The address you entered is incorrect or the State/County is not supported.",
          visible: true,
          type: "error",
        });

        return;
      }

      if (ttb) setTtbData(ttb);
      // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
      // taxDebugLog("handleAddressEnriched", {
      //   currentCompanyId: formData?.addressMeta?.companyid,
      //   resolvedPayloadCompanyId: payload?.companyid,
      //   useAnnualPropertyTax,
      //   ttbTaxAmount: ttb?.taxAmount,
      // });
      if (payload?.companyid) {
        setCompanyId(payload?.companyid);
      }

      if (payload) {
        const oldmrsid = formData?.addressMeta?.mrsid || "";
        const timestamp = Date.now();

        const formattedAddress =
          payload?.formatted_address ||
          (payload?.street_no && payload?.route_long
            ? `${payload.street_no} ${payload.route_long}${payload?.site_unit ? " #" + payload.site_unit : ""
            }, ${payload.city}, ${payload.state_code} ${payload.zip}`
            : "");

        const loanInfoMetaUpdate = {
          estimatedClosingDate: defaultEst,
          annualPropertyTaxAmount: ttb?.taxAmount,
          annualPropertyTax: useAnnualPropertyTax ? ttb?.taxAmount : "0.00",
        };

        const addressMetaUpdate = {
          companyid: payload?.companyid || "",
          address: formattedAddress,
          street_no: payload?.street_no || "",
          street_name: payload?.route_long || payload?.route || "",
          closing_state: payload?.closing_state || "",
          closing_county: payload?.closing_county || "",
          county: payload?.county_name || "",
          city: payload?.city || "",
          state: payload?.state_code || "",
          countyid: payload?.countyid || "",
          stateid: payload?.stateid || "",
          zip: payload?.zip || "",
          addressIncludesUnit: !!(
            payload?.site_unit && payload?.site_unit.trim()
          ),
          unitNumber: payload?.site_unit || "",
          mrsid: oldmrsid ? oldmrsid : `${timestamp}`,
        };

        // RHF's own store is kept in sync separately (see the "formData -> RHF
        // sync" effects below) — relying on a setFormData updater's side effect
        // to capture the resolved value here proved unreliable (the updater is
        // not guaranteed to run synchronously at this point).
        setFormData((prev) => ({
          ...prev,
          phase: "AddressSearchPreview",
          addressVerified: fromParcelSelection
            ? true
            : enrichment
              ? true
              : prev.addressVerified,
          loanInfoMeta: {
            ...(prev.loanInfoMeta || {}),
            ...loanInfoMetaUpdate,
          },
          addressMeta: addressMetaUpdate,
        }));
      }

      setShowPreviewFromChild(Boolean(showPreview));
      setIsInitialLoad(Boolean(showPreview));
      setIsCorrectProperty(true);
    },
    [setFormData, useAnnualPropertyTax, showToaster],
  );

  /**
   * ✅ Handle Address Edited (manual typing)
   */
  const handleAddressEdited = useCallback(
    (val, setters) => {
      const {
        setIsCorrectProperty,
        setIsInitialLoad,
        setUseAnnualPropertyTax,
        setUseMortgageBalance,
        setUseHomeWarranty,
      } = setters;

      if (val && val.length > 3) {
        setIsCorrectProperty(false);
        setIsInitialLoad(false);
        setUseAnnualPropertyTax(useAnnualPropertyTax);
        setUseMortgageBalance(true);
        setUseHomeWarranty(false);
      }
    },
    [useAnnualPropertyTax],
  );

  /**
   * Main Title & Escrow Data Fetcher
   */
  const getTitleEscrowData = useCallback(
    async (formData, templateformData, loanType) => {
      // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
      // taxDebugLog("getTitleEscrowData called", {
      //   loanType,
      //   isEditLoading,
      //   isCalculatingRefCurrent: isCalculatingRef.current,
      //   salesPrice: formData?.salesPrice,
      //   addressVerified: formData?.addressVerified,
      // });
      if (isEditLoading) return;
      if (isCalculatingRef.current) return; // ✅ Prevent re-entrant calls

      const salesPrice = parseCurrency(formData.salesPrice);
      if (!salesPrice || salesPrice < 50000) return;
      if (formData.addressVerified === false) return;

      // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
      // taxDebugLog("getTitleEscrowData entry", {
      //   loanType,
      //   annualPropertyTax: formData?.loanInfoMeta?.annualPropertyTax,
      //   amount: formData?.loanInfoMeta?.amount,
      //   buyerDownPayment: formData?.loanInfoMeta?.buyerDownPayment,
      //   daysInterest: formData?.prepaidsMeta?.daysInterest,
      //   principalAndInterest: formData?.paymentMeta?.principalAndInterest,
      // });

      // ✅ Create a unique key for this calculation request
      // Includes requestedDownPayment so a manually-entered down payment/loan
      // amount (Down Payment field, or the Cash-transaction loan-amount question)
      // isn't silently skipped as a "duplicate" of the prior salesPrice/loanType calc.
      const calcKey = `${formData?.addressMeta?.zip || ""}-${salesPrice}-${loanType}-${formData?.loanInfoMeta?.estimatedClosingDate || ""}-${formData?.loanInfoMeta?.requestedDownPayment || ""}`;
      if (lastCalcKeyRef.current === calcKey) return; // Skip duplicate calculations
      lastCalcKeyRef.current = calcKey;

      isCalculatingRef.current = true;

      try {
        let result = {};
        let amount = 0;

        if (loanType === "Conventional") {
          // TEMP DEBUG — remove once the loan-to-value investigation is closed.
          // taxDebugLog("getTitleEscrowData Conventional branch input", {
          //   conventionalPercent: formData?.loanInfoMeta?.conventionalPercent,
          //   requestedDownPayment: formData?.loanInfoMeta?.requestedDownPayment,
          //   buyerDownPayment: formData?.loanInfoMeta?.buyerDownPayment,
          // });
          result = getAmountConventional(formData);
          // taxDebugLog("getTitleEscrowData Conventional branch result", {
          //   ltv1: result?.ltv1,
          //   amount: result?.amount,
          //   downPayment: result?.downPayment,
          // });
          amount = result.amount;
        } else if (loanType === "FHA 203b") {
          result = getAmountFHA(formData, templateformData);
          amount = result.adjusted;
        } else if (loanType === "VA") {
          result = getAmountVA(formData, templateformData);
          amount = result.adjusted;
        } else if (loanType === "USDA") {
          result = getAmountUSDA(formData, templateformData);
          amount = result.adjusted;
        } else if (loanType === "Owner Carry") {
          result = getAmountOwnerCarry(formData, templateformData);
          amount = result.adjusted;
        } else if (loanType === "Cash") {
          result = {
            amount: "0.00",
            adjusted: "0.00",
            downPayment: formData.salesPrice,
            principalAndInterest: "0.00",
            realEstateTaxes: "0.00",
            daysInterestAmount: "0.00",
            monthsTaxes: "0",
            taxesAmount: "0.00",
            monthInsurance: "0.00",
            twoMonthsPMI: "0.00",
            fhaMipAmount: "0.00",
            vaffFinanceAmount: "0.00",
            mipFinanceAmount: "0.00",
          };
          amount = result.amount;
        }

        // console.log(
        //   "getTitleEscrowData() result:", result
        // );
        const payload = {
          closing_state: formData?.addressMeta?.state,
          closing_city: formData?.addressMeta?.city,
          closing_county: formData?.addressMeta?.county,
          county: formData?.addressMeta?.county,
          city: formData?.addressMeta?.city,
          zip: formData?.addressMeta?.zip,
          salePrice: formData.salesPrice,
          adjusted: amount,
          loanType: formData?.loanInfoMeta?.buyersLoanType,
          estStlmtDate: formData.loanInfoMeta.estimatedClosingDate,
          device: "web",
          calc: "Buyer",
          reissueyr: false,
          mrs_id: "Gf40rntTRu",
          prepared_for: formData.preparedFor,
          liab_amount: "0.00",
          sim: true,
        };

        let dataTitleEscrow = await getTitleEscrowCosts(payload);
        if (dataTitleEscrow.status === "fail") {
          showToaster({
            message:
              dataTitleEscrow.message || "Failed to fetch title & escrow data.",
            visible: true,
            type: "error",
          });
          isCalculatingRef.current = false;
          return;
        }

        let escrowTitleData = resultOfTitleEscrow(dataTitleEscrow);

        if (loanType === "Cash") {
          escrowTitleData = {
            ...escrowTitleData,
            lendersTitleAmount: "0.00",
          };
        }

        let salesTaxRate = dataTitleEscrow?.data?.salesTaxRate;
        let state = formData?.addressMeta?.state;

        let dataPrepaidPayment = await getPrepaidPaymentData(
          formData,
          result,
          templateformData,
        );

        //console.log("Response 1 : " + JSON.stringify(dataPrepaidPayment));

        let dataTwoMonthsPMI = await getTwoMonthPMI(
          formData,
          templateformData,
          result,
        );

        // console.log("Response : " + JSON.stringify(dataTwoMonthsPMI));
        let noPMI = formData?.loanInfoMeta?.noPMICheck;
        dataTwoMonthsPMI = noPMI
          ? {
            rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
            rateMonthlyPMIAmount: "0.00",
            twoMonthsPMI: "0.00",
          }
          : dataTwoMonthsPMI;

        setFormData((prev) => {
          // TEMP DEBUG — remove once the loan-to-value investigation is closed.
          // taxDebugLog("getTitleEscrowData setFormData updater prev snapshot", {
          //   loanType,
          //   prevConventionalPercent: prev?.loanInfoMeta?.conventionalPercent,
          //   prevBuyersLoanType: prev?.loanInfoMeta?.buyersLoanType,
          // });
          let updated = structuredClone(prev);

          updated.addressVerified = prev.addressVerified;
          updated.phase = "getTitleEscrowData";
          updated.main_company_name = main_company_name;

          if (mainId === 5 && state === "WA" && salesTaxRate !== 0) {
            updated = applyWaSalesTaxClosingCosts(updated, {
              escrowSettlementAmount: escrowTitleData.escrowSettlementAmount,
              lendersTitleAmount: escrowTitleData.lendersTitleAmount,
              salesTaxRate,
            });
          }

          // Backend already resolved which rows (if any) apply and at what position —
          // no state/company conditional needed here.
          updated = applyClosingCostOverrides(
            updated,
            dataTitleEscrow?.data?.closingCostOverrides
          );

          // "title" mode: on-demand rows for EscrowTitleFields.js (e.g. CA City/County
          // Transfer Tax) — not part of the closingCostsMeta template, rendered there as a
          // Payor + Amount row like Owners/Lenders Title Policy (see
          // computeTitleFeePayorAmount), and counted in computeTotals' title settlement sum.
          // Always (re)assigned, even to [], so a later non-CA address clears a stale row.
          updated.titleSettlementMeta = {
            ...(updated.titleSettlementMeta || {}),
            transferTaxRows: (dataTitleEscrow?.data?.closingCostOverrides || [])
              .filter((row) => row?.mode === "title")
              .map((row) => {
                const baseAmount = Number(row.value) || 0;
                const payor = row.payor || "";
                return {
                  label: row.label,
                  payor,
                  baseAmount,
                  amount: computeTitleFeePayorAmount(payor, baseAmount),
                };
              }),
          };

          // "closing" mode: on-demand rows for ManualSheetSection's buyerClosingCostsMeta
          // list — a flat, un-positioned list (no sr_no/index concept like closingCostsMeta),
          // so upsert by key: replace an existing row with that key, otherwise append.
          const closingModeRows = (dataTitleEscrow?.data?.closingCostOverrides || []).filter(
            (row) => row?.mode === "closing" && row?.key
          );
          if (closingModeRows.length) {
            const buyerClosingCostsMeta = Array.isArray(updated.buyerClosingCostsMeta)
              ? [...updated.buyerClosingCostsMeta]
              : [];
            closingModeRows.forEach((row) => {
              const idx = buyerClosingCostsMeta.findIndex((it) => it?.key === row.key);
              if (idx !== -1) {
                buyerClosingCostsMeta[idx] = { ...buyerClosingCostsMeta[idx], ...row };
              } else {
                buyerClosingCostsMeta.push(row);
              }
            });
            updated.buyerClosingCostsMeta = buyerClosingCostsMeta;
          }

          // console.log("[loanAmountFlow] getTitleEscrowData write-back", {
          //   loanType,
          //   prevAmount: prev?.loanInfoMeta?.amount,
          //   resultAmount: result.amount,
          //   resultDownPayment: result.downPayment,
          //   resultLtv1: result.ltv1,
          //   prevConventionalPercent: prev?.loanInfoMeta?.conventionalPercent,
          // });

          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            amount: result.amount,
            buyerDownPayment: result.downPayment,
            interestRate1:dataTitleEscrow?.data?.interestRate,
          };

          updated.titleSettlementMeta = {
            ...(updated.titleSettlementMeta || {}),
            ...escrowTitleData,
            ...(salesTaxRate !== 0 ? { salesTaxRate: salesTaxRate } : {}),
          };

          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            daysInterestAmount: dataPrepaidPayment.daysInterestAmount,
          };

          const rateMonthlyPMIManual = prev.paymentMeta?.rateMonthlyPMIManual === true;

          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            principalAndInterest: dataPrepaidPayment.principalAndInterest,
            homeownerInsurance: dataPrepaidPayment.homeOwnerInsuranceRes,
            realEstateTaxes: dataPrepaidPayment.realEstateTaxes,
            pAndI2ndTDAmount: dataPrepaidPayment.pAndI2ndTDAmount,
            ...(rateMonthlyPMIManual
              ? {}
              : {
                rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
                rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
              }),
          };

          if (loanType === "Conventional") {
            updated.prepaidsMeta = {
              ...(updated.prepaidsMeta || {}),
              twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
              mipFinanceAmount: "0.00",
              fhaMipAmount: "0.00",
              vaffFinanceAmount: "0.00",
            };

            // console.log("[loanAmountFlow] getTitleEscrowData: conventionalPercent write-back", {
            //   prevConventionalPercent: prev?.loanInfoMeta?.conventionalPercent,
            //   nextConventionalPercent: result.ltv1,
            // });

            updated.loanInfoMeta = {
              ...(updated.loanInfoMeta || {}),
              amount2: result.amount2,
              adjusted: "0.00",
              allowOwnerCarry1Check: false,
              allowOwnerCarry2Check: false,
              conventionalPercent: result.ltv1,
            };
          } else if (loanType === "FHA 203b") {
            let fhaMipAmount = "0.00";
            let adjusted = "0.00";
            if (formData?.prepaidsMeta?.fhaMipCheck === true) {
              fhaMipAmount = result.FhaMipFin2;
              adjusted =
                parseCurrency(result.FhaMipFin) +
                (parseCurrency(result.amount) || "0.00");
            } else {
              fhaMipAmount = result.FhaMipFin;
              adjusted = result.adjusted;
            }

            updated.loanInfoMeta = {
              ...(updated.loanInfoMeta || {}),
              adjusted: adjusted,
              allowOwnerCarry1Check: false,
              allowOwnerCarry2Check: false,
            };
            updated.prepaidsMeta = {
              ...(updated.prepaidsMeta || {}),
              twoMonthsPMI: "0.00",
              fhaMip: result.FhaMipFin1,
              fhaMipAmount: fhaMipAmount,
              FhaMipFin: result.FhaMipFin,
              FhaMipFin2: result.FhaMipFin2,
              daysInterestAmount: result.daysInterestAmount,
              mipFinanceAmount: "0.00",
              vaffFinanceAmount: "0.00",
            };

            if (!rateMonthlyPMIManual) {
              updated.paymentMeta = {
                ...(updated.paymentMeta || {}),
                rateMonthlyPMI: result.rateValue,
                rateMonthlyPMIAmount: result.monthlyRate,
              };
            }

          } else if (loanType === "VA") {
            updated.loanInfoMeta = {
              ...(updated.loanInfoMeta || {}),
              adjusted: result.adjusted,
              allowOwnerCarry1Check: false,
              allowOwnerCarry2Check: false,
            };
            updated.prepaidsMeta = {
              ...(updated.prepaidsMeta || {}),
              twoMonthsPMI: "0.00",
              vaffFinance: result.financedFee,
              vaffFinanceFee: result.VaFfFin,
              vaffFinanceRate: result.vaffFinanceRate,
              vaffFinanceAmount: result.cashDue,
              mipFinanceAmount: "0.00",
              fhaMipAmount: "0.00",
            };
          } else if (loanType === "USDA") {
            updated.loanInfoMeta = {
              ...(updated.loanInfoMeta || {}),
              adjusted: result.adjusted,
              amount: result.amount,
              allowOwnerCarry1Check: false,
              allowOwnerCarry2Check: false,
            };
            updated.prepaidsMeta = {
              ...(updated.prepaidsMeta || {}),
              twoMonthsPMI: "0.00",
              UsdaMipFinance: result.UsdaMipFinance,
              mipFinance: result.mipFinance,
              mipFinanceAmount: result.mipFinanceAmount,
              fhaMipAmount: "0.00",
              vaffFinanceAmount: "0.00",
            };
          } else if (loanType === "Cash") {
            updated.loanInfoMeta = {
              ...(updated.loanInfoMeta || {}),
              adjusted: result.adjusted,
              amount: result.amount,
              downPayment: result.downPayment,
              allowOwnerCarry1Check: false,
              allowOwnerCarry2Check: false,
            };

            updated.paymentMeta = {
              ...(updated.paymentMeta || {}),
              principalAndInterest: result.principalAndInterest,
              realEstateTaxes: result.realEstateTaxes,
              pAndI2ndTDAmount: "0.00",
              rateMonthlyPMI: "0.00",
              rateMonthlyPMIAmount: "0.00",
              monthlyExpense1Amount: "0.00",
              monthlyExpense2Amount: "0.00",
              homeownerInsurance: "0.00",
            };

            updated.prepaidsMeta = {
              ...(updated.prepaidsMeta || {}),
              twoMonthsPMI: result.twoMonthsPMI,
              mipFinanceAmount: result.mipFinanceAmount,
              fhaMipAmount: result.fhaMipAmount,
              vaffFinanceAmount: result.vaffFinanceAmount,
              taxesAmount: result.taxesAmount,
              daysInterestAmount: result.daysInterestAmount,
              costsAmount: "0.00",
            };
          } else if (loanType === "Owner Carry") {
            updated.loanInfoMeta = {
              ...(updated.loanInfoMeta || {}),
              adjusted: "0.00",
              amount: result.amount,
              amount2: result.amount2,
              downPayment: result.downPayment,
              allowOwnerCarry1Check: true,
            };

            updated.prepaidsMeta = {
              ...(updated.prepaidsMeta || {}),
              twoMonthsPMI: result.twoMonthsPMI,
              mipFinanceAmount: "0.00",
              fhaMipAmount: "0.00",
              vaffFinanceAmount: "0.00",
            };
          }

          const total = computeTotals(
          updated,
          templateformData?.commission_buyer !== false,
        );

          updated.totalClosingMeta = {
            ...(updated.totalClosingMeta || {}),
            totalCommission: total.totalCommission,
            totalClosingCosts: total.totalClosingCosts,
            totalPrepaids: total.totalPrepaids,
            totalMonthlyPayment: total.totalMonthlyPayment,
            totalMonthlyPaymentOld: total.totalMonthlyPaymentOld,
            totalInvestment: total.totalInvestment,
          };

          // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
          // taxDebugLog("getTitleEscrowData result", {
          //   loanType,
          //   annualPropertyTax: updated?.loanInfoMeta?.annualPropertyTax,
          //   amount: updated?.loanInfoMeta?.amount,
          //   buyerDownPayment: updated?.loanInfoMeta?.buyerDownPayment,
          //   daysInterest: updated?.prepaidsMeta?.daysInterest,
          //   daysInterestAmount: updated?.prepaidsMeta?.daysInterestAmount,
          //   principalAndInterest: updated?.paymentMeta?.principalAndInterest,
          // });

          return updated;
        });
        // RHF's own store is kept in sync separately (see the "formData -> RHF
        // sync" effects below) rather than here — capturing the updater's
        // result via a closure variable proved unreliable (the updater is not
        // guaranteed to run synchronously at this point).
      } catch (error) {
        console.error("Error in getTitleEscrowData:", error);
      } finally {
        // ✅ Always release the lock, even on error
        setTimeout(() => {
          isCalculatingRef.current = false;
        }, 100);
      }
    },
    [
      isEditLoading,
      templateformData,
      main_company_name,
      showToaster,
      setFormData,
      mainId,
    ],
  );

  /**
   * ✅ Handle Quick Mode Initialization
   */
  const handleQuickModeInit = useCallback(
    async (zipData, postalCode, setters) => {
      const { setShowManualSheet } = setters;

      if (!zipData || zipData.status === "fail") {
        showToaster({
          message:
            "The address you entered is incorrect or the State/County is not supported.",
          visible: true,
          type: "error",
        });
        return;
      }

      const body = zipData?.data;
      if (!body || zipData.status !== "success") return;

      const initialFormData = {
        addressVerified: true,
        loanInfoMeta: {
          estimatedClosingDate: defaultEst,
          annualPropertyTax: body?.ttb?.taxAmount ?? 0.0,
          annualPropertyTaxAmount: body?.ttb?.taxAmount ?? 0.0,
        },
        addressMeta: {
          city: body.city || "",
          stateid: body.stateid,
          state: body.state_code || "",
          countyid: body.countyid,
          county: body.county_name || "",
          zip: postalCode || "",
          unitNumber: body.site_unit || "",
          addressIncludesUnit: body.addressIncludesUnit || "",
          closing_state: body.closing_state || "",
          closing_county: body.closing_county,
          closing_city: body.closing_city,
        },
      };

      setFormData((prev) => ({
        ...prev,
        ...initialFormData,
        addressMeta: {
          ...(prev.addressMeta || {}),
          ...initialFormData.addressMeta,
          // Quick mode: keep user's companyid, not ZIP-derived companyid
          companyid: prev.addressMeta?.companyid || "",
        },
      }));

      await new Promise((resolve) => setTimeout(resolve, 300));

      const mergedFormData = {
        ...formData,
        ...initialFormData,
      };

      await getTitleEscrowData(
        mergedFormData,
        templateformData,
        "Conventional",
      );

      await new Promise((resolve) => setTimeout(resolve, 500));

      const latestFormData = getValues() || {};

      const sp = parseCurrency(latestFormData.salesPrice);
      if (
        templateformData?.commission_buyer !== false &&
        sp &&
        sp >= 50000 &&
        latestFormData?.compensationMeta
      ) {
        const comp = latestFormData.compensationMeta;
        const sellerPercent = parseCurrency(comp.sellerBrokerCompensation);
        const buyerPercent = parseCurrency(comp.buyerBrokerCompensation);
        const sellerAmount = parseCurrency(comp.sellerBrokerCompensationAmount);
        const buyerAmount = parseCurrency(comp.buyerBrokerCompensationAmount);

        const hasPercents = sellerPercent > 0 || buyerPercent > 0;
        const noAmounts = sellerAmount === 0 && buyerAmount === 0;

        if (hasPercents && noAmounts) {
          // Functional updater, not setFormData(computeCommission(latestFormData, true))
          // — latestFormData comes from getValues() (react-hook-form's own
          // cache), a separate source of truth from the formData React
          // state that nothing keeps in sync for fields like
          // buyerClosingCostsMeta. computeCommission shallow-spreads
          // whatever it's given, so writing its result directly would
          // silently replace the real, current formData (and anything
          // updated on it since) with this stale RHF snapshot.
          setFormData((prev) => computeCommission(prev, true));
        }
      }

      setTimeout(() => {
        setShowManualSheet(true);
      }, 100);
    },
    [
      formData,
      setFormData,
      getTitleEscrowData,
      templateformData,
      showToaster,
      getValues,
    ],
  );

  /**
   * Handle Loan Amount calculations
   */
  const handleLoanAmount = useCallback(
    (snapshot, loanType) => {
      let result = {};

      // TEMP DEBUG — remove once the loan-to-value investigation is closed.
      // taxDebugLog("handleLoanAmount called", {
      //   loanType,
      //   snapshotBuyersLoanType: snapshot?.loanInfoMeta?.buyersLoanType,
      //   snapshotConventionalPercent: snapshot?.loanInfoMeta?.conventionalPercent,
      // });

      result = getAmountConventional(snapshot);

      let dataTwoMonthsPMI = getTwoMonthPMI(snapshot, templateformData, result);

      const dataPrepaidPayment = getPrepaidPaymentData(
        snapshot,
        result,
        templateformData,
      );

      // console.log("Response 2 : " + JSON.stringify(dataPrepaidPayment));

      let dataPSecondTd = {};
      if (loanType === "secondLoan") {
        dataPSecondTd = getPSecondTd(snapshot, result);
      }


      setFormData((prev) => {
        let updated = structuredClone(prev);

        updated.addressVerified = prev.addressVerified;
        updated.phase = "handleLoanAmount";
        updated.loanInfoMeta = {
          ...(updated.loanInfoMeta || {}),
          amount: result.amount,
          amount2: result.amount2,
          buyerDownPayment: result.downPayment,
          conventionalPercent: result.ltv1,
        };

        if (loanType === "firstLoan") {
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            taxesAmount: dataPrepaidPayment.prepaidMonthTaxes,
            insAmount: dataPrepaidPayment.monthInsurance,
            daysInterestAmount: dataPrepaidPayment.daysInterestAmount,
            twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
          };
        } else if (loanType === "secondLoan") {
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
            daysInterestAmount: dataPrepaidPayment.daysInterestAmount,
          };
        }

        if (loanType === "firstLoan") {
          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            principalAndInterest: dataPrepaidPayment.principalAndInterest,
            homeownerInsurance: dataPrepaidPayment.homeOwnerInsuranceRes,
            realEstateTaxes: dataPrepaidPayment.realEstateTaxes,
            rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
            rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
          };
        } else if (loanType === "secondLoan") {
          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            pAndI2ndTDAmount:
              dataPSecondTd?.pnintrate ||
              dataPSecondTd?.pAndI2ndTDAmount ||
              "0.00",
            rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
            rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
          };
        }

        const total = computeTotals(
          updated,
          templateformData?.commission_buyer !== false,
        );
        updated.totalClosingMeta = {
          ...(updated.totalClosingMeta || {}),
          totalCommission: total.totalCommission,
          totalClosingCosts: total.totalClosingCosts,
          totalPrepaids: total.totalPrepaids,
          totalMonthlyPayment: total.totalMonthlyPayment,
          totalMonthlyPaymentOld: total.totalMonthlyPaymentOld,
          totalInvestment: total.totalInvestment,
        };

        return updated;
      });
      // RHF's own store is kept in sync separately (see the "formData -> RHF
      // sync" effects below).
    },
    [templateformData, setFormData],
  );

  /**
   * Handle Loan Type Change (main calculation dispatcher)
   */
  const handleCalAmount = useCallback(
    (snapshot) => {
      // ✅ Guard against re-entrant calls
      if (isCalculatingRef.current) return;

      switch (snapshot.loanInfoMeta.buyersLoanType) {
        case "Conventional":
          getTitleEscrowData(snapshot, templateformData, "Conventional");
          break;
        case "FHA 203b":
          getTitleEscrowData(snapshot, templateformData, "FHA 203b");
          break;
        case "VA":
          getTitleEscrowData(snapshot, templateformData, "VA");
          break;
        case "USDA":
          getTitleEscrowData(snapshot, templateformData, "USDA");
          break;
        case "Cash":
          getTitleEscrowData(snapshot, templateformData, "Cash");
          break;
        case "Owner Carry":
          getTitleEscrowData(snapshot, templateformData, "Owner Carry");
          break;
        default:
          break;
      }
    },
    [getTitleEscrowData, templateformData],
  );

  /**
   * Effects: State Settings, County Settings, National Settings
   */
  useEffect(() => {
    if (isEditLoading) return;
    if (!formData?.addressMeta?.stateid || !mainId) return;

    // console.log("fetchStateSettings 1 : ", formData.addressMeta.stateid);
    const fetchStateSettings = async () => {
      try {
        // console.log("fetchStateSettings 2 : ", mainId);
        const normalizeArray = (data) =>
          Array.isArray(data) ? data : data ? [data] : [];

        const [dataProration, dataImpound] = await Promise.all([
          getStateSettings({
            stateid: formData.addressMeta.stateid,
            type: "buyer_proration",
            maincompanyid: mainId,
          }),
          getStateSettings({
            stateid: formData.addressMeta.stateid,
            type: "buyer_impound",
            maincompanyid: mainId,
          }),
        ]);

        const prorationList = normalizeArray(dataProration?.data);
        const impoundList = normalizeArray(dataImpound?.data);
        const prepaidTax = setPrepaidData(formData, impoundList[0]);

        setFormData((prev) => ({
          ...prev,
          prepaidsMeta: {
            ...(prev.prepaidsMeta || {}),
            monthsTaxes: prepaidTax.monthsTaxes,
          //  daysInterest: prepaidTax.daysInterest,
          },
        }));

        setTemplateformData((prev) => ({
          ...(prev || {}),
          phase: "proration 2",
          data1Proration: prorationList[0],
          data2Proration: prorationList[1] || {},
          dataImpound: impoundList[0],
        }));
      } catch (error) {
        console.error("Error fetching state settings:", error);
      }
    };

    fetchStateSettings();
  }, [
    formData?.addressMeta?.stateid,
    mainId,
    isEditLoading,
    setFormData,
    setTemplateformData,
  ]);

  /**
   * Effect: Re-derive Months Taxes / Days Interest when the estimated closing
   * date changes, using the already-fetched impound schedule (no re-fetch).
   */
  useEffect(() => {
    if (isEditLoading) return;
    if (formData?.loanInfoMeta?.buyersLoanType === "Cash") return;
    const dataImpound = templateformData?.dataImpound;
    if (!dataImpound || !Object.keys(dataImpound).length) return;

    const prepaidTax = setPrepaidData(formData, dataImpound);
    // console.log("Recalculating prepaidTax:", prepaidTax);
    setFormData((prev) => {
      if (
        prev.prepaidsMeta?.monthsTaxes === prepaidTax.monthsTaxes &&
        prev.prepaidsMeta?.daysInterest === prepaidTax.daysInterest
      ) {
        return prev;
      }
      return {
        ...prev,
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          monthsTaxes: prepaidTax.monthsTaxes,
        //  daysInterest: prepaidTax.daysInterest,
        },
      };
    });
  }, [
    formData?.loanInfoMeta?.estimatedClosingDate,
    formData?.loanInfoMeta?.buyersLoanType,
    templateformData?.dataImpound,
    isEditLoading,
    setFormData,
  ]);

  useEffect(() => {
    if (!formData?.addressMeta?.countyid || !mainId) return;

    // console.log("fetchCountySettings 1 : ", formData.addressMeta.countyid);
    const fetchCountySettings = async () => {
      try {
        // console.log("fetchCountySettings 2 : ", mainId);
        const dataCountySetting = await getCountySettings({
          stateid: formData.addressMeta.stateid,
          countyid: formData.addressMeta.countyid,
          maincompanyid: mainId,
        });

        const dataArray = Array.isArray(dataCountySetting?.data)
          ? dataCountySetting.data
          : [];

        if (dataArray.length === 0) {
          return;
        }

        const firstCounty = dataArray[0];
        const dataCountyArr = {
          maxloanamount: firstCounty?.maxloanamount ?? "0.00",
          vamaxloanamount: firstCounty?.vamaxloanamount ?? "0.00",
        };

        setTemplateformData((prev) => ({
          ...(prev || {}),
          phase: "county 2",
          dataCounty: dataCountyArr,
        }));
      } catch (error) {
        console.error("Failed to fetch county settings:", error);
      }
    };

    fetchCountySettings();
  }, [formData?.addressMeta?.countyid, mainId, setTemplateformData]);

  useEffect(() => {
    if (!mainId) return;
    // console.log("fetchNationalSettings mainId 0 :", mainId);
    const fetchNationalSettings = async () => {
      // console.log("fetchNationalSettings mainId 1 :", mainId);
      try {
        // console.log("fetchNationalSettings mainId 2 :", mainId);
        const res = await getNationalSettings({ maincompanyid: mainId });
        // console.log("fetchNationalSettings response:", res);
        const nationalData =
          Array.isArray(res?.data) && res.data.length > 0
            ? res.data[0]
            : res?.data && typeof res.data === "object"
              ? res.data
              : res && typeof res === "object"
                ? res
                : {};

        // console.log("[conventionalPercent] fetchNationalSettings result", {
        //   rawRes: res,
        //   resolvedNationalData: nationalData,
        //   ltvFromApi:
        //     nationalData?.conventional_loans?.conventional_loan_defaults
        //       ?.resale_conventional_loan_ltv_percent,
        // });

        setFormData((prev) => {
          const next = {
            ...prev,
            loanInfoMeta: {
              ...prev.loanInfoMeta,
              conventionalPercent:
                nationalData?.conventional_loans?.conventional_loan_defaults
                  ?.resale_conventional_loan_ltv_percent,
            },
          };
          // console.log("[conventionalPercent] fetchNationalSettings write", {
          //   prevConventionalPercent: prev?.loanInfoMeta?.conventionalPercent,
          //   nextConventionalPercent:
          //     next.loanInfoMeta.conventionalPercent,
          // });
          return next;
        });

        setTemplateformData((prev) => ({
          ...(prev || {}),
          phase: "national 2",
          dataNational: nationalData,
        }));
      } catch (error) {
        console.error("Error fetching national settings:", error);
      }
    };

    fetchNationalSettings();
  }, [mainId, setFormData, setTemplateformData]);

  /**
   * Effects: FHA MIP, VA FF, USDA MIP finance checks
   */
  useEffect(() => {
    if (isEditLoading) return;
    if (isCalculatingRef.current) return; // ✅ Guard against loops

    let fhaMipAmount = "0.00";
    let adjusted = "0.00";

    if (formData?.prepaidsMeta?.fhaMipCheck === true) {
      fhaMipAmount = formData?.prepaidsMeta?.FhaMipFin2;
      adjusted =
        parseCurrency(formData?.prepaidsMeta?.FhaMipFin) +
        (parseCurrency(formData.loanInfoMeta?.amount) || 0);
    } else {
      fhaMipAmount = formData?.prepaidsMeta?.FhaMipFin;
      adjusted = parseCurrency(formData.loanInfoMeta?.amount) || 0;
    }

    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);

    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );

    setFormData((prev) => {
      const updated = {
        ...prev,
        loanInfoMeta: {
          ...(prev.loanInfoMeta || {}),
          adjusted: adjusted.toFixed(2),
        },
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          fhaMipAmount: fhaMipAmount,
          daysInterestAmount: daysInterestAmount,
        },
      };
      const total = computeTotals(
        updated,
        templateformData?.commission_buyer !== false,
      );
      updated.totalClosingMeta = {
        ...(updated.totalClosingMeta || {}),
        totalCommission: total.totalCommission,
        totalClosingCosts: total.totalClosingCosts,
        totalPrepaids: total.totalPrepaids,
        totalMonthlyPayment: total.totalMonthlyPayment,
        totalMonthlyPaymentOld: total.totalMonthlyPaymentOld,
        totalInvestment: total.totalInvestment,
      };
      return updated;
    });
    // RHF's own store is kept in sync separately (see the "formData -> RHF
    // sync" effects below).
  }, [
    formData?.prepaidsMeta?.fhaMipCheck,
    formData.loanInfoMeta?.interestRate1,
    formData.prepaidsMeta?.daysInterest,
    formData?.prepaidsMeta?.FhaMipFin,
    formData?.prepaidsMeta?.FhaMipFin2,
    formData.loanInfoMeta?.amount,
    isEditLoading,
    setFormData,
  ]);

  useEffect(() => {
    if (isEditLoading) return;
    if (isCalculatingRef.current) return; // ✅ Guard against loops
    if (formData?.loanInfoMeta?.buyersLoanType !== "VA") return;

    const vaResult = getAmountVA(formData, templateformData);
    const adjusted = parseCurrency(vaResult.adjusted);

    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);

    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );

    setFormData((prev) => {
      const updated = {
        ...prev,
        loanInfoMeta: {
          ...(prev.loanInfoMeta || {}),
          adjusted: adjusted.toFixed(2),
        },
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          vaffFinanceAmount: vaResult.cashDue,
          vaffFinance: vaResult.financedFee,
          daysInterestAmount: daysInterestAmount,
        },
      };
      const total = computeTotals(
        updated,
        templateformData?.commission_buyer !== false,
      );
      updated.totalClosingMeta = {
        ...(updated.totalClosingMeta || {}),
        totalCommission: total.totalCommission,
        totalClosingCosts: total.totalClosingCosts,
        totalPrepaids: total.totalPrepaids,
        totalMonthlyPayment: total.totalMonthlyPayment,
        totalMonthlyPaymentOld: total.totalMonthlyPaymentOld,
        totalInvestment: total.totalInvestment,
      };
      return updated;
    });
    // RHF's own store is kept in sync separately (see the "formData -> RHF
    // sync" effects below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.prepaidsMeta?.vaffFinanceCheck,
    formData?.loanInfoMeta?.buyersLoanType,
    // Depend only on the specific data this effect actually reads, not the
    // whole templateformData object — that reference changes on almost every
    // unrelated update (county/national settings, phase, etc.), which was
    // re-firing this effect (and its setFormData call) far more often than
    // needed and feeding a loop with ManualSheetSection's RHF sync effects.
    templateformData?.dataNational,
    templateformData?.commission_buyer,
    isEditLoading,
    setFormData,
  ]);

  useEffect(() => {
    if (isEditLoading) return;
    if (isCalculatingRef.current) return; // ✅ Guard against loops
    if (formData?.loanInfoMeta?.buyersLoanType !== "USDA") return;

    const usdaResult = getAmountUSDA(formData, templateformData);
    const adjusted = parseCurrency(usdaResult.adjusted);

    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);

    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );

    setFormData((prev) => {
      const updated = {
        ...prev,
        loanInfoMeta: {
          ...(prev.loanInfoMeta || {}),
          adjusted: adjusted.toFixed(2),
        },
        prepaidsMeta: {
          ...(prev.prepaidsMeta || {}),
          mipFinanceAmount: usdaResult.mipFinanceAmount,
          mipFinance: usdaResult.mipFinance,
          daysInterestAmount: daysInterestAmount,
        },
      };
      const total = computeTotals(
        updated,
        templateformData?.commission_buyer !== false,
      );
      updated.totalClosingMeta = {
        ...(updated.totalClosingMeta || {}),
        totalCommission: total.totalCommission,
        totalClosingCosts: total.totalClosingCosts,
        totalPrepaids: total.totalPrepaids,
        totalMonthlyPayment: total.totalMonthlyPayment,
        totalMonthlyPaymentOld: total.totalMonthlyPaymentOld,
        totalInvestment: total.totalInvestment,
      };
      return updated;
    });
    // RHF's own store is kept in sync separately (see the "formData -> RHF
    // sync" effects below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.prepaidsMeta?.mipFinanceCheck,
    formData?.loanInfoMeta?.buyersLoanType,
    // See the identical comment in the VA effect above for why this is
    // narrowed instead of depending on the whole templateformData object.
    templateformData?.dataNational,
    templateformData?.commission_buyer,
    isEditLoading,
    setFormData,
  ]);

  /**
   * Effect: Calculate estimated Tax Prorations
   */
  useEffect(() => {
    if (isEditLoading) return;
    if (isCalculatingRef.current) return; // ✅ Guard against loops

    const currentFormData = formData;

    const taxAmountFromForm = parseCurrency(
      currentFormData?.loanInfoMeta?.annualPropertyTax ||
      currentFormData?.loanInfoMeta?.annualPropertyTaxAmount ||
      0,
    );
    const taxAmountFromTtb = parseCurrency(ttbData?.taxAmount || 0);

    let taxAmount = taxAmountFromForm;
    if (taxAmountFromForm <= 0 && taxAmountFromTtb > 0) {
      taxAmount = taxAmountFromTtb;
    } else if (taxAmountFromForm > 0) {
      taxAmount = taxAmountFromForm;
    }

    const data1Proration = templateformData?.data1Proration || {};

    if (taxAmount <= 0 || !Object.keys(data1Proration).length) return;

    const formDataForCalculation = {
      ...currentFormData,
      loanInfoMeta: {
        ...(currentFormData?.loanInfoMeta || {}),
        annualPropertyTax:
          taxAmount > 0
            ? taxAmount.toString()
            : currentFormData?.loanInfoMeta?.annualPropertyTax || "0.00",
      },
    };

    const estimatedTax = getBuyerEstimatedTax(
      formDataForCalculation,
      templateformData,
    );

    if (!estimatedTax || isNaN(estimatedTax.estimatedTaxProrations)) return;

    setFormData((prev) => {
      const newValue = estimatedTax.estimatedTaxProrations;
      const currentValue = parseCurrency(
        prev.paymentMeta?.estimatedTaxProrations || 0,
      );

      if (Math.abs(newValue - currentValue) < 0.01) {
        return prev;
      }

      return {
        ...prev,
        paymentMeta: {
          ...(prev.paymentMeta || {}),
          estimatedTaxProrations: newValue,
        },
      };
    });
  }, [
    ttbData?.taxAmount,
    templateformData,
    formData?.salesPrice,
    formData?.loanInfoMeta?.estimatedClosingDate,
    formData?.loanInfoMeta?.annualPropertyTax,
    formData?.loanInfoMeta?.annualPropertyTaxAmount,
    isEditLoading,
    useAnnualPropertyTax,
    setFormData,
  ]);

  /**
   * Effect: Trigger recalculation when sales price changes
   * ✅ FIXED: Use primitive dependency instead of formData object
   */
  useEffect(() => {
    // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
    // taxDebugLog("salesPrice/buyersLoanType recalculation effect firing", {
    //   isEditLoading,
    //   isCalculatingRefCurrent: isCalculatingRef.current,
    //   addressVerified: formData?.addressVerified,
    //   buyersLoanType: formData?.loanInfoMeta?.buyersLoanType,
    //   salesPrice: formData?.salesPrice,
    // });
    if (isEditLoading) return;
    if (isCalculatingRef.current) return; // ✅ Guard against loops
    if (!formData?.addressVerified) return;
    if (!formData?.loanInfoMeta?.buyersLoanType) return;

    const salesPrice = parseCurrency(formData.salesPrice);
    if (!salesPrice || salesPrice < 50000) return;

    // dataNational (PMI/MIP rate tables) often arrives after the first
    // calculation already ran, since it's an independent async fetch. Once
    // it lands, bypass the dedup key so this effect actually recalculates
    // instead of silently no-opping against an identical salesPrice/loanType.
    const hasNational = !!(
      templateformData?.dataNational &&
      Object.keys(templateformData.dataNational).length
    );
    if (hasNational && !hadNationalDataRef.current) {
      hadNationalDataRef.current = true;
      lastCalcKeyRef.current = "";
    }

    getTitleEscrowData(
      formData,
      templateformData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.salesPrice,
    isEditLoading,
    formData?.addressVerified,
    formData?.loanInfoMeta?.buyersLoanType,
    formData?.loanInfoMeta?.requestedDownPayment,
    templateformData?.dataNational,
  ]);

  /**
   * Effect: Trigger recalculation on noPMICheck toggle
   * ✅ FIXED: Use primitive dependency instead of formData object
   */
  useEffect(() => {
    if (isEditLoading) return;
    if (isCalculatingRef.current) return; // ✅ Guard against loops
    if (!formData?.addressVerified) return;
    if (!formData?.loanInfoMeta?.buyersLoanType) return;

    const salesPrice = parseCurrency(formData.salesPrice);
    if (!salesPrice || salesPrice < 50000) return;

    getTitleEscrowData(
      formData,
      templateformData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData?.loanInfoMeta?.noPMICheck,
    isEditLoading,
    formData?.addressVerified,
    formData?.loanInfoMeta?.buyersLoanType,
    formData?.salesPrice,
  ]);

  /**
   * Effects: keep react-hook-form's own store in sync with formData.
   *
   * Several calculation paths above only ever call setFormData — never
   * setValue. Meanwhile useUserTemplate's reset() stamps RHF's canonical
   * values with defaults (false/""/"0.00") before any address or calculation
   * exists, and nothing else corrects them. ManualSheetSection has its own
   * "watch RHF, sync into formData" effects (for loanInfoMeta, addressMeta,
   * addressVerified) that then copy those stale RHF defaults back over
   * whatever was just correctly computed here.
   *
   * These effects close that gap the reliable way: by watching formData
   * itself (guaranteed to reflect the true latest state once React commits
   * it) rather than trying to capture a setFormData updater's result via a
   * closure variable, which is not guaranteed to run synchronously and was
   * confirmed unreliable in practice.
   *
   * formData.* objects get a brand-new reference on almost every update
   * elsewhere in the app (structuredClone recreates the whole tree even when
   * only one bucket actually changed), so these effects re-fire far more
   * often than the underlying data actually changes. Pushing an unchanged
   * value into RHF via setValue still causes its watchers to re-notify,
   * which can feed back into formData and re-fire these effects again —
   * syncIfChanged breaks that loop by comparing against RHF's current value
   * first and skipping the setValue call when nothing actually changed.
   */
  const syncIfChanged = useCallback(
    (path, value) => {
      if (typeof setValue !== "function" || typeof getValues !== "function") {
        return;
      }
      const current = getValues(path);
      if (JSON.stringify(current) === JSON.stringify(value)) return;
      setValue(path, value, { shouldDirty: true, shouldTouch: true });
    },
    [setValue, getValues],
  );

  useEffect(() => {
    syncIfChanged("addressVerified", formData?.addressVerified);
  }, [formData?.addressVerified, syncIfChanged]);

  useEffect(() => {
    if (!formData?.addressMeta) return;
    syncIfChanged("addressMeta", formData.addressMeta);
  }, [formData?.addressMeta, syncIfChanged]);

  useEffect(() => {
    if (!formData?.loanInfoMeta) return;
    syncIfChanged("loanInfoMeta", formData.loanInfoMeta);
  }, [formData?.loanInfoMeta, syncIfChanged]);

  useEffect(() => {
    if (!formData?.prepaidsMeta) return;
    syncIfChanged("prepaidsMeta", formData.prepaidsMeta);
  }, [formData?.prepaidsMeta, syncIfChanged]);

  useEffect(() => {
    if (!formData?.paymentMeta) return;
    syncIfChanged("paymentMeta", formData.paymentMeta);
  }, [formData?.paymentMeta, syncIfChanged]);

  useEffect(() => {
    if (!formData?.titleSettlementMeta) return;
    syncIfChanged("titleSettlementMeta", formData.titleSettlementMeta);
  }, [formData?.titleSettlementMeta, syncIfChanged]);

  useEffect(() => {
    if (!formData?.totalClosingMeta) return;
    syncIfChanged("totalClosingMeta", formData.totalClosingMeta);
  }, [formData?.totalClosingMeta, syncIfChanged]);

  return {
    getTitleEscrowData,
    handleLoanAmount,
    handleCalAmount,
    handleAddressEnriched,
    handleAddressEdited,
    handleQuickModeInit,
  };
};
